// org-id for organice: giving a headline an address.
//
// `org-id-get-create` in Emacs does one small thing with large consequences. It
// puts an `:ID:` property in a headline's drawer, and only then can anything
// point at that headline: an `[[id:...]]` link, an org-roam node, a backlink
// (see lib/org_roam.js, where an `:ID:` is precisely what makes a headline a
// node). Without one, a heading can only be referred to by its text, which
// changes.
//
// Idempotence is the whole contract, and the reason this is a module rather than
// two lines in the click handler. `org-id-get-create` returns the id an entry
// already has instead of minting a second one, because an id that changes is
// worse than no id at all: every link already written to the old value stops
// resolving, silently, along with every export and every cached org-roam row
// that carries it. Emacs only overrides an existing id when called with FORCE,
// which nothing here does.

import { propertyValue, setProperty } from './kontrakt';

/** The property org-id writes, spelled as org-mode spells it. */
export const ORG_ID_PROPERTY = 'ID';

const UUID_TEMPLATE = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';

/**
 * A fresh org-id: a lowercase RFC 4122 version 4 uuid, which is what Emacs
 * mints under the default `org-id-method` of `uuid`.
 *
 * `crypto.randomUUID` is the right source but exists only in a secure context,
 * so it is missing over plain http - a phone pointed at the dev server, say.
 * `getRandomValues` is the same randomness without that restriction, and is
 * present wherever `window.crypto` is at all.
 *
 * Neither available throws rather than falling back to `Math.random`. A
 * collision-prone id is not a degraded id: two headlines sharing one is a
 * silent corruption of every link written to either, which is the single
 * failure this module exists to prevent. Refusing is the honest outcome, and no
 * browser this app runs in gets there.
 */
export const newOrgId = () => {
  const crypto = typeof window !== 'undefined' ? window.crypto : undefined;

  if (crypto && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().toLowerCase();
  }

  if (!crypto || typeof crypto.getRandomValues !== 'function') {
    throw new Error('This browser offers no secure randomness, so no :ID: was created.');
  }

  // A byte modulo 16 is uniform because 256 is a multiple of 16.
  const randomNibble = () => crypto.getRandomValues(new Uint8Array(1))[0] % 16;

  // The 4 and the 8..b of a v4 uuid are fixed by the spec, hence the template's
  // literal '4' and the 'y' that only ever becomes one of those four digits.
  return UUID_TEMPLATE.replace(/[xy]/g, (placeholder) => {
    const nibble = randomNibble();
    return (placeholder === 'x' ? nibble : (nibble & 0x3) | 0x8).toString(16);
  });
};

/** The org-id a header already carries, or null when it carries none. */
export const orgIdOfHeader = (header) => (header ? propertyValue(header, ORG_ID_PROPERTY) : null);

/**
 * `org-id-get-create` on one header: `{ id, propertyListItems, created }`.
 *
 * `created` is false and `propertyListItems` is the untouched original when the
 * header already has an id - so a caller can skip dirtying the file, and no
 * existing link is invalidated. `generate` is injectable for the tests, which
 * need a predictable id.
 */
export const orgIdGetCreate = (header, generate = newOrgId) => {
  const propertyListItems = header.get('propertyListItems');
  const existing = orgIdOfHeader(header);

  if (existing) return { id: existing, propertyListItems, created: false };

  const id = generate();
  return {
    id,
    propertyListItems: setProperty(propertyListItems, ORG_ID_PROPERTY, id),
    created: true,
  };
};
